/**
 * Support & Customer Service Type Definitions
 *
 * This file contains all TypeScript types, interfaces, and enums for the
 * customer service and support system.
 *
 * @module types/support
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Ticket Types
 *
 * Categorizes support tickets by the nature of the inquiry or issue.
 * Covers all phases of the halal certification journey.
 */
export enum TicketType {
  /** General inquiries about halal certification process */
  GENERAL_INQUIRY = 'general_inquiry',

  /** Help with application form, documents, or submission */
  APPLICATION_SUPPORT = 'application_support',

  /** Questions about audit preparation, execution, or findings */
  AUDIT_QUESTION = 'audit_question',

  /** Training enrollment, scheduling, or course-related support */
  TRAINING_SUPPORT = 'training_support',

  /** Payment processing, invoices, or billing inquiries */
  PAYMENT_ISSUE = 'payment_issue',

  /** Certificate issuance, download, or verification requests */
  CERTIFICATE_REQUEST = 'certificate_request',

  /** Certificate renewal process support */
  RENEWAL_SUPPORT = 'renewal_support',

  /** Platform technical issues, bugs, or access problems */
  TECHNICAL_ISSUE = 'technical_issue',

  /** Service quality complaints or feedback */
  COMPLAINT = 'complaint',

  /** General feedback or suggestions */
  FEEDBACK = 'feedback',
}

/**
 * Ticket Status
 *
 * Represents the current state of a support ticket in the workflow.
 */
export enum TicketStatus {
  /** Ticket has been created but not yet assigned or worked on */
  OPEN = 'open',

  /** Ticket is currently being worked on by support staff */
  IN_PROGRESS = 'in_progress',

  /** Waiting for customer to provide more information or respond */
  WAITING_CUSTOMER = 'waiting_customer',

  /** Waiting for internal team (e.g., consultant, trainer, admin) */
  WAITING_INTERNAL = 'waiting_internal',

  /** Issue resolved, awaiting customer confirmation */
  RESOLVED = 'resolved',

  /** Ticket is closed and archived */
  CLOSED = 'closed',
}

/**
 * Ticket Priority
 *
 * Determines the urgency and SLA (Service Level Agreement) for tickets.
 */
export enum TicketPriority {
  /** Low priority - Response within 48 hours */
  LOW = 'low',

  /** Medium priority - Response within 24 hours */
  MEDIUM = 'medium',

  /** High priority - Response within 8 hours */
  HIGH = 'high',

  /** Urgent - Response within 2 hours, critical issues */
  URGENT = 'urgent',
}

/**
 * Knowledge Base Categories
 *
 * Organizes KB articles and FAQs by topic area.
 */
export enum KnowledgeBaseCategory {
  /** Getting started guides, onboarding, first steps */
  GETTING_STARTED = 'getting_started',

  /** Application submission, document requirements, form filling */
  APPLICATION_PROCESS = 'application_process',

  /** Pre-audit checklist, audit preparation, what to expect */
  AUDIT_PREPARATION = 'audit_preparation',

  /** Training courses, enrollment, certificates */
  TRAINING = 'training',

  /** Payment methods, invoices, billing */
  PAYMENTS_INVOICES = 'payments_invoices',

  /** Certificate issuance, download, verification, renewal */
  CERTIFICATES = 'certificates',

  /** Renewal process, re-certification */
  RENEWAL = 'renewal',

  /** Platform usage, technical help, troubleshooting */
  TECHNICAL = 'technical',

  /** Policies, terms, compliance, legal */
  POLICIES = 'policies',
}

// ============================================================================
// CORE INTERFACES
// ============================================================================

/**
 * Support Ticket
 *
 * Primary entity representing a customer support request.
 * Supports two-way communication through messages.
 */
export interface SupportTicket {
  /** Unique identifier */
  id: string;

  /** Human-readable ticket number (e.g., "TKT-2026-00123") */
  ticketNumber: string;

  /** Company that created the ticket */
  companyId: string;
  companyName: string;

  /** Ticket classification */
  type: TicketType;
  status: TicketStatus;
  priority: TicketPriority;

  /** Ticket content */
  subject: string;
  description: string;

  // -------------------------------------------------------------------------
  // Assignment
  // -------------------------------------------------------------------------

  /** Support staff assigned to handle this ticket */
  assignedTo?: string; // User ID
  assignedToName?: string;
  assignedToRole?: 'consultant' | 'support_staff' | 'trainer' | 'expert';
  assignedAt?: Date;

  // -------------------------------------------------------------------------
  // Related Entities (Optional Linking)
  // -------------------------------------------------------------------------

  /** Link to related application for context */
  relatedApplicationId?: string;

  /** Link to related invoice/payment for context */
  relatedInvoiceId?: string;

  /** Link to related certificate for context */
  relatedCertificateId?: string;

  // -------------------------------------------------------------------------
  // Tracking & Timestamps
  // -------------------------------------------------------------------------

  /** User who created the ticket */
  createdBy: string;
  createdByName: string;
  createdAt: Date;

  /** Last update timestamp */
  updatedAt: Date;

  /** When the ticket was resolved */
  resolvedAt?: Date;

  /** When the ticket was closed */
  closedAt?: Date;

  // -------------------------------------------------------------------------
  // SLA (Service Level Agreement) Tracking
  // -------------------------------------------------------------------------

  /** Deadline to resolve the ticket based on priority */
  slaDeadline?: Date;

  /** Deadline for first response */
  responseDeadline?: Date;

  /** When the first response was provided */
  firstResponseAt?: Date;

  // -------------------------------------------------------------------------
  // Metadata
  // -------------------------------------------------------------------------

  /** Tags for categorization and search */
  tags: string[];

  /** File attachments */
  attachments: TicketAttachment[];

  /** Number of messages in the conversation */
  messageCount: number;

  /** Customer satisfaction rating (1-5 stars) */
  rating?: number;

  /** Customer feedback on resolution */
  ratingFeedback?: string;
}

/**
 * Ticket Message
 *
 * Represents a single message in the ticket conversation thread.
 * Enables two-way communication between customer and support staff.
 */
export interface TicketMessage {
  /** Unique identifier */
  id: string;

  /** Parent ticket ID */
  ticketId: string;

  /** Message author */
  authorId: string;
  authorName: string;
  authorRole: 'company' | 'consultant' | 'auditor' | 'admin' | 'support_staff' | 'trainer';

  /** Message content */
  message: string;

  /** Internal notes not visible to the company */
  isInternal: boolean;

  /** File attachments */
  attachments: TicketAttachment[];

  /** Message timestamps */
  createdAt: Date;
  editedAt?: Date;
}

/**
 * Ticket Attachment
 *
 * File attached to a ticket or message.
 */
export interface TicketAttachment {
  /** Unique identifier */
  id: string;

  /** Original file name */
  fileName: string;

  /** File size in bytes */
  fileSize: number;

  /** MIME type (e.g., "image/png", "application/pdf") */
  fileType: string;

  /** URL to download/view the file */
  url: string;

  /** User who uploaded the file */
  uploadedBy: string;

  /** Upload timestamp */
  uploadedAt: Date;
}

/**
 * Knowledge Base Article
 *
 * Help article with markdown content.
 * Designed for self-service support.
 */
export interface KnowledgeBaseArticle {
  /** Unique identifier */
  id: string;

  /** URL-friendly identifier (e.g., "how-to-apply-for-certification") */
  slug: string;

  /** Article category */
  category: KnowledgeBaseCategory;

  /** Article title */
  title: string;

  /** Short description (shown in lists) */
  excerpt: string;

  /** Full article content in Markdown format */
  content: string;

  /** Tags for categorization and search */
  tags: string[];

  /** Article author */
  author: string;

  /** Publication timestamps */
  publishedAt: Date;
  updatedAt: Date;

  /** Analytics */
  viewCount: number;
  helpful: number;
  notHelpful: number;

  /** Featured articles appear prominently */
  featured: boolean;

  /** Related article IDs for cross-referencing */
  relatedArticles: string[];
}

/**
 * FAQ (Frequently Asked Question)
 *
 * Simple question-answer pair.
 * Lighter weight than full articles.
 */
export interface FAQ {
  /** Unique identifier */
  id: string;

  /** FAQ category */
  category: KnowledgeBaseCategory;

  /** Question text */
  question: string;

  /** Answer text (can include markdown) */
  answer: string;

  /** Display order within category */
  order: number;

  /** Analytics */
  helpful: number;
  notHelpful: number;

  /** Related article IDs for deeper reading */
  relatedArticles: string[];
}

/**
 * Support Statistics
 *
 * Dashboard metrics for support performance.
 */
export interface SupportStats {
  /** Total tickets ever created */
  totalTickets: number;

  /** Currently open tickets */
  openTickets: number;

  /** Tickets currently being worked on */
  inProgressTickets: number;

  /** Tickets resolved today */
  resolvedToday: number;

  /** Average time to first response (in hours) */
  avgResponseTime: number;

  /** Average time to resolution (in hours) */
  avgResolutionTime: number;

  /** Average customer satisfaction rating (1-5) */
  satisfactionRating: number;

  /** Tickets breakdown by type */
  ticketsByType: Record<TicketType, number>;

  /** Tickets breakdown by priority */
  ticketsByPriority: Record<TicketPriority, number>;
}

// ============================================================================
// REQUEST/RESPONSE TYPES (for API)
// ============================================================================

/**
 * Create Ticket Request
 *
 * Data required to create a new support ticket.
 */
export interface CreateTicketRequest {
  type: TicketType;
  priority: TicketPriority;
  subject: string;
  description: string;
  relatedApplicationId?: string;
  relatedInvoiceId?: string;
  relatedCertificateId?: string;
  tags?: string[];
}

/**
 * Update Ticket Request
 *
 * Data that can be updated on an existing ticket.
 */
export interface UpdateTicketRequest {
  status?: TicketStatus;
  priority?: TicketPriority;
  subject?: string;
  description?: string;
  tags?: string[];
}

/**
 * Ticket Filters
 *
 * Filters for querying tickets.
 */
export interface TicketFilters {
  status?: TicketStatus | TicketStatus[];
  type?: TicketType | TicketType[];
  priority?: TicketPriority | TicketPriority[];
  companyId?: string;
  assignedTo?: string;
  createdAfter?: Date;
  createdBefore?: Date;
  search?: string;
}

/**
 * Close Ticket Request
 *
 * Data required to close a ticket.
 */
export interface CloseTicketRequest {
  /** Optional customer satisfaction rating (1-5 stars) */
  rating?: number;

  /** Optional feedback on resolution */
  feedback?: string;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Ticket Summary
 *
 * Lightweight version of SupportTicket for list views.
 */
export type TicketSummary = Pick<
  SupportTicket,
  | 'id'
  | 'ticketNumber'
  | 'companyName'
  | 'type'
  | 'status'
  | 'priority'
  | 'subject'
  | 'createdAt'
  | 'updatedAt'
  | 'messageCount'
  | 'assignedToName'
>;

/**
 * Article Summary
 *
 * Lightweight version of KnowledgeBaseArticle for list views.
 */
export type ArticleSummary = Pick<
  KnowledgeBaseArticle,
  'id' | 'slug' | 'category' | 'title' | 'excerpt' | 'tags' | 'viewCount' | 'featured'
>;
